<template>
  <!-- Mobile overlay backdrop -->
  <div v-if="isMobileOpen" class="sidebar-backdrop" @click="closeMobile"></div>

  <aside
    class="admin-sidebar"
    :class="{
      'is-expanded': isExpanded,
      'is-mobile-open': isMobileOpen,
    }"
    @mouseenter="!isMobile && onEnter()"
    @mouseleave="!isMobile && onLeave()"
  >
    <div class="sidebar-brand">
      <span class="sidebar-brand-logo-badge">
        <img src="/logo/LogoIcon.png" alt="Applypa" class="sidebar-brand-logo" />
      </span>
      <span class="sidebar-label sidebar-brand-text">Applypa Admin</span>
      <button
        v-if="!isMobile"
        type="button"
        class="sidebar-pin-btn"
        :class="{ 'sidebar-pin-btn--active': isPinned }"
        @click="togglePin"
        :aria-pressed="isPinned"
        :title="isPinned ? 'Unpin sidebar' : 'Pin sidebar open'"
      >
        <i class="fas fa-thumbtack"></i>
      </button>
      <button v-if="isMobile" class="sidebar-close-btn" @click="closeMobile">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <nav class="sidebar-nav">

      <a
        class="sidebar-link"
        :class="{ 'sidebar-link--active': isDashboardActive }"
        href="#"
        @click.prevent="handleNavClick('/admin-dashboard')"
      >
        <i class="fas fa-chart-line"></i>
        <span class="sidebar-label">Dashboard</span>
      </a>

      <a
        class="sidebar-link"
        :class="{ 'sidebar-link--active': isActivePath('/admin-dashboard/jobhunter-dashboard') }"
        href="#"
        @click.prevent="handleNavClick('/admin-dashboard/jobhunter-dashboard')"
      >
        <i class="fas fa-briefcase"></i>
        <span class="sidebar-label">Job Hunters</span>
      </a>

      <a
        class="sidebar-link"
        :class="{ 'sidebar-link--active': isActivePath('/admin-dashboard/users') }"
        href="#"
        @click.prevent="handleNavClick('/admin-dashboard/users')"
      >
        <i class="fas fa-users"></i>
        <span class="sidebar-label">Users</span>
      </a>

      <!-- Job Posts group (collapsible) -->
      <div class="sidebar-group">
        <a
          class="sidebar-link"
          :class="{ 'sidebar-link--active': isActivePath('/admin-dashboard/job-posts') }"
          href="#"
          @click.prevent="toggleJobPostsGroup"
          :aria-expanded="isJobPostsOpen"
          data-test="job-posts-group"
        >
          <i class="fas fa-layer-group"></i>
          <span class="sidebar-label">Job Posts</span>
          <i
            class="fas fa-chevron-right sidebar-caret sidebar-label"
            :class="{ 'sidebar-caret--open': isJobPostsOpen }"
          ></i>
        </a>

        <div v-if="isJobPostsOpen && isNavVisible" class="sidebar-submenu">
          <a
            class="sidebar-link sidebar-sublink"
            :class="{ 'sidebar-link--active': route.path === '/admin-dashboard/job-posts' }"
            href="#"
            @click.prevent="handleNavClick('/admin-dashboard/job-posts')"
          >
            <i class="fas fa-chart-pie"></i>
            <span class="sidebar-label">Overview</span>
          </a>
          <a
            class="sidebar-link sidebar-sublink"
            :class="{ 'sidebar-link--active': isActivePath('/admin-dashboard/job-posts/daily') }"
            href="#"
            @click.prevent="handleNavClick('/admin-dashboard/job-posts/daily')"
          >
            <i class="fas fa-calendar-day"></i>
            <span class="sidebar-label">Daily scrape</span>
          </a>
        </div>
      </div>

      <!-- Applications group (collapsible) -->
      <div class="sidebar-group">
        <a
          class="sidebar-link"
          :class="{ 'sidebar-link--active': isActivePath('/admin-dashboard/applications') || isActivePath('/admin-dashboard/auto-apply') }"
          href="#"
          @click.prevent="toggleApplicationsGroup"
          :aria-expanded="isApplicationsOpen"
          data-test="applications-group"
        >
          <i class="fas fa-paper-plane"></i>
          <span class="sidebar-label">Applications</span>
          <i
            class="fas fa-chevron-right sidebar-caret sidebar-label"
            :class="{ 'sidebar-caret--open': isApplicationsOpen }"
          ></i>
        </a>

        <div v-if="isApplicationsOpen && isNavVisible" class="sidebar-submenu">
          <a
            class="sidebar-link sidebar-sublink"
            :class="{ 'sidebar-link--active': isActivePath('/admin-dashboard/applications') }"
            href="#"
            @click.prevent="handleNavClick('/admin-dashboard/applications')"
          >
            <i class="fas fa-list-ul"></i>
            <span class="sidebar-label">List</span>
          </a>
          <a
            class="sidebar-link sidebar-sublink"
            :class="{ 'sidebar-link--active': isActivePath('/admin-dashboard/auto-apply') }"
            href="#"
            @click.prevent="handleNavClick('/admin-dashboard/auto-apply')"
          >
            <i class="fas fa-robot"></i>
            <span class="sidebar-label">Auto Apply</span>
          </a>
        </div>
      </div>

      <!-- Billing group (collapsible) -->
      <div class="sidebar-group">
        <a
          class="sidebar-link"
          :class="{ 'sidebar-link--active': isActivePath('/admin-dashboard/billing') }"
          href="#"
          @click.prevent="toggleBillingGroup"
          :aria-expanded="isBillingOpen"
          data-test="billing-group"
        >
          <i class="fas fa-file-invoice-dollar"></i>
          <span class="sidebar-label">Billing</span>
          <i
            class="fas fa-chevron-right sidebar-caret sidebar-label"
            :class="{ 'sidebar-caret--open': isBillingOpen }"
          ></i>
        </a>

        <div v-if="isBillingOpen && isNavVisible" class="sidebar-submenu">
          <a
            class="sidebar-link sidebar-sublink"
            :class="{ 'sidebar-link--active': route.path === '/admin-dashboard/billing' }"
            href="#"
            @click.prevent="handleNavClick('/admin-dashboard/billing')"
          >
            <i class="fas fa-calendar-alt"></i>
            <span class="sidebar-label">Monthly</span>
          </a>
          <a
            class="sidebar-link sidebar-sublink"
            :class="{ 'sidebar-link--active': isActivePath('/admin-dashboard/billing/users') }"
            href="#"
            @click.prevent="handleNavClick('/admin-dashboard/billing/users')"
          >
            <i class="fas fa-user-tag"></i>
            <span class="sidebar-label">Per user</span>
          </a>
        </div>
      </div>

      <a
        class="sidebar-link"
        :class="{ 'sidebar-link--active': isActivePath('/admin-dashboard/email-events') }"
        href="#"
        @click.prevent="handleNavClick('/admin-dashboard/email-events')"
      >
        <i class="fas fa-envelope"></i>
        <span class="sidebar-label">Email Events</span>
      </a>

      <a
        class="sidebar-link"
        :class="{ 'sidebar-link--active': isActivePath('/admin-dashboard/profile-optimizer') }"
        href="#"
        @click.prevent="handleNavClick('/admin-dashboard/profile-optimizer')"
      >
        <i class="fas fa-wand-magic-sparkles"></i>
        <span class="sidebar-label">Profile Optimizer</span>
      </a>

      <a
        class="sidebar-link"
        :class="{ 'sidebar-link--active': isActivePath('/admin-dashboard/activity-logs') }"
        href="#"
        @click.prevent="handleNavClick('/admin-dashboard/activity-logs')"
      >
        <i class="fas fa-list-alt"></i>
        <span class="sidebar-label">Activity Logs</span>
      </a>

      <a
        class="sidebar-link"
        :class="{ 'sidebar-link--active': isActivePath('/admin-dashboard/support-tickets') }"
        href="#"
        @click.prevent="handleNavClick('/admin-dashboard/support-tickets')"
      >
        <i class="fas fa-ticket-alt"></i>
        <span class="sidebar-label">Tickets</span>
      </a>

      <!-- Proxy group (collapsible) -->
      <div class="sidebar-group">
        <a
          class="sidebar-link"
          :class="{ 'sidebar-link--active': isActivePath('/admin-dashboard/proxies') || isActivePath('/admin-dashboard/scrapers') }"
          href="#"
          @click.prevent="toggleProxyGroup"
          :aria-expanded="isProxyOpen"
        >
          <i class="fas fa-network-wired"></i>
          <span class="sidebar-label">Proxy</span>
          <i
            class="fas fa-chevron-right sidebar-caret sidebar-label"
            :class="{ 'sidebar-caret--open': isProxyOpen }"
          ></i>
        </a>

        <div v-if="isProxyOpen && isNavVisible" class="sidebar-submenu">
          <a
            class="sidebar-link sidebar-sublink"
            :class="{ 'sidebar-link--active': isActivePath('/admin-dashboard/proxies') }"
            href="#"
            @click.prevent="handleNavClick('/admin-dashboard/proxies')"
          >
            <i class="fas fa-list-ul"></i>
            <span class="sidebar-label">List</span>
          </a>
          <a
            class="sidebar-link sidebar-sublink"
            :class="{ 'sidebar-link--active': isActivePath('/admin-dashboard/scrapers') }"
            href="#"
            @click.prevent="handleNavClick('/admin-dashboard/scrapers')"
          >
            <i class="fas fa-spider"></i>
            <span class="sidebar-label">Scrapers</span>
          </a>
        </div>
      </div>

      <a
        class="sidebar-link"
        :class="{ 'sidebar-link--active': isActivePath('/admin-dashboard/celery') }"
        href="#"
        @click.prevent="handleNavClick('/admin-dashboard/celery')"
      >
        <i class="fas fa-cogs"></i>
        <span class="sidebar-label">Celery</span>
      </a>

      <a
        class="sidebar-link"
        :class="{ 'sidebar-link--active': isActivePath('/admin-dashboard/landing-page') }"
        href="#"
        @click.prevent="handleNavClick('/admin-dashboard/landing-page')"
      >
        <i class="fas fa-desktop"></i>
        <span class="sidebar-label">Landing Page</span>
      </a>
    </nav>

    <div class="sidebar-footer">
      <a class="sidebar-link" href="#" @click.prevent="toggleTheme">
        <i :class="theme === 'dark' ? 'fas fa-sun' : 'fas fa-moon'"></i>
        <span class="sidebar-label">{{ theme === 'dark' ? 'Light mode' : 'Dark mode' }}</span>
      </a>
      <a class="sidebar-link" href="#" @click.prevent="handleNavClick('/job-filters')">
        <i class="fas fa-arrow-left"></i>
        <span class="sidebar-label">Back to App</span>
      </a>
    </div>
  </aside>
</template>

<script>
import { computed, ref, onMounted, onBeforeUnmount } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useTheme } from "@/composables/useTheme";

export default {
  name: "AdminSidebar",
  emits: ["sidebar-change", "mobile-open-change"],

  setup(props, { emit, expose }) {
    const route = useRoute();
    const router = useRouter();
    const { theme, toggleTheme } = useTheme();

    const isDashboardActive = computed(
      () => route.path === "/admin-dashboard"
    );

    function isActivePath(path) {
      return route.path.startsWith(path);
    }

    // ── Desktop hover expand ───────────────────────────────────────────────
    const isHovered = ref(false);
    let enterTimer = null;
    let leaveTimer = null;

    const PIN_STORAGE_KEY = "sidebar_pinned";
    const isPinned = ref(localStorage.getItem(PIN_STORAGE_KEY) === "true");
    const isExpanded = computed(() => isHovered.value || isPinned.value);

    function onEnter() {
      clearTimeout(leaveTimer);
      enterTimer = setTimeout(() => {
        isHovered.value = true;
        emit("sidebar-change", true);
      }, 250);
    }

    function onLeave() {
      if (isPinned.value) return;
      clearTimeout(enterTimer);
      leaveTimer = setTimeout(() => {
        isHovered.value = false;
        emit("sidebar-change", false);
      }, 150);
    }

    function togglePin() {
      isPinned.value = !isPinned.value;
      localStorage.setItem(PIN_STORAGE_KEY, isPinned.value ? "true" : "false");
      if (isPinned.value) {
        clearTimeout(leaveTimer);
        isHovered.value = true;
        emit("sidebar-change", true);
      } else {
        onLeave();
      }
    }

    // ── Mobile drawer ──────────────────────────────────────────────────────
    const isMobile = ref(false);
    const isMobileOpen = ref(false);
    // Guard: true while the slide-in animation is still running (300 ms)
    const isAnimating = ref(false);

    function checkMobile() {
      isMobile.value = window.innerWidth < 768;
    }

    function openMobile() {
      if (isMobileOpen.value) return;
      isMobileOpen.value = true;
      isAnimating.value = true;
      emit("mobile-open-change", true);
      document.body.style.overflow = "hidden";
      // Animation duration matches the CSS transition (300 ms)
      setTimeout(() => {
        isAnimating.value = false;
      }, 320);
    }

    function closeMobile() {
      isMobileOpen.value = false;
      isAnimating.value = false;
      emit("mobile-open-change", false);
      document.body.style.overflow = "";
    }

    // ── Collapsible groups ─────────────────────────────────────────────────
    // Labels are only readable when the rail is expanded (desktop) or the
    // drawer is open (mobile) — sub-menus follow the same rule.
    const isNavVisible = computed(() => isExpanded.value || isMobileOpen.value);

    // Open by default when the user is already on a page inside the group.
    const isProxyOpen = ref(
      route.path.startsWith("/admin-dashboard/proxies") ||
      route.path.startsWith("/admin-dashboard/scrapers")
    );

    const isApplicationsOpen = ref(
      route.path.startsWith("/admin-dashboard/applications") ||
      route.path.startsWith("/admin-dashboard/auto-apply")
    );

    const isJobPostsOpen = ref(route.path.startsWith("/admin-dashboard/job-posts"));

    const isBillingOpen = ref(route.path.startsWith("/admin-dashboard/billing"));

    function toggleBillingGroup() {
      if (!isNavVisible.value) return; // collapsed rail — inert, like other links
      isBillingOpen.value = !isBillingOpen.value;
    }

    function toggleJobPostsGroup() {
      if (!isNavVisible.value) return; // collapsed rail — inert, like other links
      isJobPostsOpen.value = !isJobPostsOpen.value;
    }

    function toggleApplicationsGroup() {
      if (!isNavVisible.value) return; // collapsed rail — inert, like other links
      isApplicationsOpen.value = !isApplicationsOpen.value;
    }

    function toggleProxyGroup() {
      if (!isNavVisible.value) return; // collapsed rail — inert, like other links
      isProxyOpen.value = !isProxyOpen.value;
    }

    // ── Nav click helpers ──────────────────────────────────────────────────

    /**
     * Top-level nav click (Dashboard, Users, Activity Logs, Back to App).
     * On mobile: close sidebar then navigate after the close animation.
     * On desktop: collapse sidebar then navigate.
     * Blocked while slide-in animation is running.
     */
    function handleNavClick(to) {
      if (isAnimating.value) return; // block during slide-in

      if (isMobile.value) {
        if (!isMobileOpen.value) return; // sidebar not open — ignore
        closeMobile();
        setTimeout(() => router.push(to), 300);
        return;
      }

      if (!isExpanded.value) return; // collapsed & not pinned/hovered — inert

      if (isHovered.value && !isPinned.value) {
        clearTimeout(enterTimer);
        clearTimeout(leaveTimer);
        isHovered.value = false;
        emit("sidebar-change", false);
        setTimeout(() => router.push(to), 320);
      } else {
        router.push(to);
      }
    }

    onMounted(() => {
      checkMobile();
      window.addEventListener("resize", checkMobile);
    });

    onBeforeUnmount(() => {
      window.removeEventListener("resize", checkMobile);
      document.body.style.overflow = "";
    });

    expose({ openMobile, closeMobile, isMobileOpen });

    return {
      route,
      theme,
      toggleTheme,
      isDashboardActive,
      isHovered,
      isExpanded,
      isPinned,
      togglePin,
      isMobile,
      isMobileOpen,
      isActivePath,
      isNavVisible,
      isProxyOpen,
      toggleProxyGroup,
      isApplicationsOpen,
      toggleApplicationsGroup,
      isJobPostsOpen,
      toggleJobPostsGroup,
      isBillingOpen,
      toggleBillingGroup,
      onEnter,
      onLeave,
      openMobile,
      closeMobile,
      handleNavClick,
    };
  },
};
</script>

<style scoped>
.sidebar-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 149;
  animation: fadeIn 0.25s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}

.admin-sidebar {
  width: 56px;
  flex-shrink: 0;
  background: hsl(var(--card));
  border-right: 1px solid hsl(var(--border));
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  transition: width 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 150;
}

.admin-sidebar.is-expanded {
  width: 220px;
}

@media (max-width: 767px) {
  .admin-sidebar {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 260px !important;
    height: 100vh !important;
    height: 100dvh !important;
    transform: translateX(-100%) !important;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    z-index: 150 !important;
  }

  .admin-sidebar.is-mobile-open {
    transform: translateX(0) !important;
  }
}

.sidebar-brand {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.75rem 8px 0.5rem;
  font-size: 1rem;
  font-weight: 700;
  color: hsl(var(--foreground));
  border-bottom: 1px solid hsl(var(--border));
  white-space: nowrap;
  overflow: hidden;
}

.sidebar-brand-logo-badge {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sidebar-brand-logo {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sidebar-brand-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-close-btn {
  margin-left: auto;
  margin-right: 14px;
  background: hsl(var(--foreground) / 0.08);
  border: none;
  color: hsl(var(--muted-foreground));
  width: 28px;
  height: 28px;
  border-radius: 7px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
}

.sidebar-close-btn:hover {
  background: hsl(var(--foreground) / 0.15);
  color: hsl(var(--foreground));
}

.sidebar-pin-btn {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: hsl(var(--muted-foreground));
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  opacity: 0;
  pointer-events: none;
  transition: background 0.15s, color 0.15s, transform 0.15s, opacity 0.15s ease;
}

.is-expanded .sidebar-pin-btn {
  opacity: 1;
  pointer-events: auto;
  transition: opacity 0.2s ease 0.1s;
}

.sidebar-pin-btn:hover {
  background: hsl(var(--accent));
  color: hsl(var(--foreground));
}

.sidebar-pin-btn--active {
  color: hsl(var(--primary));
  background: hsl(var(--primary) / 0.1);
  transform: rotate(-45deg);
}

.sidebar-nav {
  flex: 1;
  padding: 1rem 8px;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  overflow-y: auto;
  overflow-x: hidden;
}

.sidebar-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 10px;
  border-radius: 8px;
  color: hsl(var(--muted-foreground));
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
  overflow: hidden;
  cursor: pointer;
}

.sidebar-link:not(.is-expanded .sidebar-link):not(.is-mobile-open .sidebar-link) {
  cursor: not-allowed;
  opacity: 0.7;
}

.sidebar-link:hover {
  background: hsl(var(--accent));
  color: hsl(var(--foreground));
}

.sidebar-link--active {
  background: hsl(var(--primary) / 0.15);
  color: hsl(var(--primary));
}

.sidebar-link i:first-child {
  width: 20px;
  text-align: center;
  font-size: 0.85rem;
  flex-shrink: 0;
}

.sidebar-label {
  opacity: 0;
  transition: opacity 0.15s ease 0.05s;
  overflow: hidden;
}

.is-expanded .sidebar-label,
.is-mobile-open .sidebar-label {
  opacity: 1;
  transition: opacity 0.2s ease 0.1s;
}

@media (max-width: 767px) {
  .sidebar-label {
    opacity: 1 !important;
  }
}

.sidebar-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.sidebar-caret {
  margin-left: auto;
  font-size: 0.65rem !important;
  transition: transform 0.2s ease, opacity 0.15s ease 0.05s;
}

.sidebar-caret--open {
  transform: rotate(90deg);
}

.sidebar-submenu {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-left: 19px;
  padding-left: 10px;
  border-left: 1px solid hsl(var(--border));
}

.sidebar-sublink {
  padding: 0.45rem 10px;
  font-size: 0.8125rem;
}

.sidebar-footer {
  padding: 0.75rem 8px;
  border-top: 1px solid hsl(var(--border));
}
</style>